import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Races from './pages/Races';
import RaceDetail from './pages/RaceDetail';
import MyPicks from './pages/MyPicks';
import Leaderboard from './pages/Leaderboard';
import Results from './pages/Results';
import Tidbits from './pages/Tidbits';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import RegistrationCodes from './pages/admin/RegistrationCodes';
import DependentRequests from './pages/admin/DependentRequests';
import ManageDependents from './pages/admin/ManageDependents';
import CreateUser from './pages/admin/CreateUser';
import ManageUserPicks from './pages/admin/ManageUserPicks';
import ManageTidbits from './pages/admin/ManageTidbits';
import ManageRaces from './pages/admin/ManageRaces';
import SubmitResults from './pages/admin/SubmitResults';
import HorseRacingIntegration from './pages/admin/HorseRacingIntegration';
import Notifications from './pages/admin/Notifications';
import ComposeEmail from './pages/admin/ComposeEmail';
import ChangePassword from './pages/ChangePassword';
import AccountSettings from './pages/AccountSettings';
import Chat from './pages/Chat';
import Help from './pages/Help';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isElevated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isElevated) {
    return (
      <div className="container">
        <h1>Access Denied</h1>
        <p>You do not have permission to access this page.</p>
        <p>Elevated privileges required.</p>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Header />
          <main className="main-content">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/races"
                element={
                  <ProtectedRoute>
                    <Races />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/races/:raceId"
                element={
                  <ProtectedRoute>
                    <RaceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-picks"
                element={
                  <ProtectedRoute>
                    <MyPicks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/results"
                element={
                  <ProtectedRoute>
                    <Results />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tidbits"
                element={
                  <ProtectedRoute>
                    <Tidbits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <Help />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/registration-codes"
                element={
                  <AdminRoute>
                    <RegistrationCodes />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/dependent-requests"
                element={
                  <AdminRoute>
                    <DependentRequests />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/manage-dependents"
                element={
                  <AdminRoute>
                    <ManageDependents />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/create-user"
                element={
                  <AdminRoute>
                    <CreateUser />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/manage-user-picks"
                element={
                  <AdminRoute>
                    <ManageUserPicks />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/tidbits"
                element={
                  <AdminRoute>
                    <ManageTidbits />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/races"
                element={
                  <AdminRoute>
                    <ManageRaces />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/races/:raceId/results"
                element={
                  <AdminRoute>
                    <SubmitResults />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/horse-racing"
                element={
                  <AdminRoute>
                    <HorseRacingIntegration />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <AdminRoute>
                    <Notifications />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/compose-email"
                element={
                  <AdminRoute>
                    <ComposeEmail />
                  </AdminRoute>
                }
              />
            </Routes>
          </main>
          <footer className="footer">
            <p>&copy; 2026 ARFTCHC - Horse Racing Picks</p>
          </footer>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
